// packages/chatbot/src/hooks/controller/useConversationSelection.ts

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ConversationSummary, ChatMemoryScope } from '../../types';
import { NEW_CHAT_ID } from '../../utils/constants';
import type { HistoryStrategy } from './useHistoryStrategy';
import { clearChatMemory } from '../../utils/memory';

/**
 * Persistence methods required by this hook.
 */
type ConversationPersistence = {
  loadSavedConversationId: () => string | null;
  saveConversationId: (id: string) => void;
  clearSavedConversationId: () => void;
};

/**
 * Props for the useConversationSelection hook.
 * Note: This hook is no longer responsible for clearing the agent-kit state or setting messages.
 * It manages the selected ID, and other hooks react to that change.
 */
type Params = {
  isWidget: boolean;
  mode: 'full' | 'standalone';
  scope?: ChatMemoryScope;
  agentId: string | null;
  sessionReady: boolean;
  historyStrategy: HistoryStrategy;
  conversationPersistence: ConversationPersistence;
  initialActiveId?: string | null;
  onBeforeSelect?: () => void;
  
  // These are no longer needed as useConversation handles transcript loading reactively.
  // getCachedTranscript?: (id: string) => UIMessage[] | undefined;
  // cacheTranscript?: (id: string, msgs: UIMessage[]) => void;
};

/**
 * The public API returned by the hook.
 */
type Result = {
  activeConversationId: string | null;
  onSelectConversation: (id: string | null) => void;
  adoptActiveConversationId: (id: string) => void;
  isNewChatPending: boolean;
  isHistoryLoading: boolean; // Kept for API stability, but loading is now managed by useConversation
  displayedConversations: ConversationSummary[];
};

/**
 * Manages the UI state for conversation selection, including which conversation is active,
 * handling user selections from a list, and persisting the selection.
 */
export function useConversationSelection({
  mode,
  scope,
  agentId,
  historyStrategy,
  conversationPersistence,
  initialActiveId = NEW_CHAT_ID,
  onBeforeSelect,
}: Params): Result {
  const [activeConversationId, setActiveConversationId] = useState<string | null>(initialActiveId);
  const [isNewChatPending, setIsNewChatPending] = useState<boolean>(initialActiveId === NEW_CHAT_ID);
  
  // Track whether the user has made an explicit selection to prevent auto-selection from overriding it.
  const userSelectedRef = useRef(false);

  // Effect to determine and set the initial conversation ID on load.
  useEffect(() => {
    // Do not run if the user has already interacted with the list.
    if (userSelectedRef.current) return;

    // Determine the target ID from the prop (resolved by controller) or from storage.
    const target =
      initialActiveId && initialActiveId !== NEW_CHAT_ID 
        ? initialActiveId 
        : conversationPersistence.loadSavedConversationId() || NEW_CHAT_ID;

    setActiveConversationId(target);
    setIsNewChatPending(target === NEW_CHAT_ID);

    // This effect only runs when the agent or initial target changes, setting the stage
    // for the useConversation hook to load the actual transcript.
  }, [agentId, initialActiveId, conversationPersistence]);


  /**
   * Handles a user clicking on a conversation in the list (including "New Chat").
   * This is the primary way the activeConversationId changes via user interaction.
   */
  const onSelectConversation = useCallback(
    (id: string | null) => {
      onBeforeSelect?.();
      userSelectedRef.current = true;
      
      // The change to activeConversationId will be picked up by the controller and passed
      // to the useConversation hook, which will then handle resetting its own state.

      // Case 1: User selected "New Chat".
      if (!id || id === NEW_CHAT_ID) {
        conversationPersistence.clearSavedConversationId();
        // Clear transient message drafts for this scope as well.
        if (scope) {
          clearChatMemory(scope, agentId === null ? undefined : agentId);
        }
        setActiveConversationId(NEW_CHAT_ID);
        setIsNewChatPending(true);
        return;
      }

      // Case 2: User selected an existing conversation.
      setActiveConversationId(id);
      setIsNewChatPending(false);
      conversationPersistence.saveConversationId(id);
    },
    [onBeforeSelect, conversationPersistence, scope, agentId]
  );

  /**
   * Adopts the real server-provided conversation ID after the first turn of a new chat.
   */
  const adoptActiveConversationId = useCallback(
    (id: string) => {
      if (!id) return;
      setActiveConversationId(id);
      setIsNewChatPending(false);
      conversationPersistence.saveConversationId(id);
    },
    [conversationPersistence]
  );

  /**
   * Memoized list of conversations to display in the UI (e.g., sidebar).
   * It correctly injects the "New Chat" placeholder when appropriate.
   * The synthetic summary for a just-created chat is now handled in the controller.
   */
  const displayedConversations: ConversationSummary[] = useMemo(() => {
    const base = (historyStrategy.list.items || []) as ConversationSummary[];
    if (mode !== 'full') return base;

    // Show the "New Chat" placeholder if we are in that state, or if there's no history.
    const shouldInjectPlaceholder = isNewChatPending || base.length === 0;
    if (!shouldInjectPlaceholder) return base;
    
    // Avoid duplicating the placeholder if the active one is already in the list
    if (base.some(c => c.id === NEW_CHAT_ID)) return base;

    const nowIso = new Date().toISOString();
    const newChatPlaceholder: ConversationSummary = {
      id: NEW_CHAT_ID,
      created_at: nowIso,
      updated_at: nowIso,
      first_user_message_preview: 'New Chat',
      agent_id: agentId || '',
      user_id: '',
    };

    return [newChatPlaceholder, ...base];
  }, [historyStrategy.list.items, isNewChatPending, mode, agentId]);

  return {
    activeConversationId,
    onSelectConversation,
    adoptActiveConversationId,
    isNewChatPending,
    isHistoryLoading: false, // Loading is handled by useConversation
    displayedConversations,
  };
}